import { computed, defineComponent, inject, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import html_iway from "./template.iway.html?raw";
import { orderServiceKey } from "../../../infrastructure/gfo_api/order_service";
import { currentOrderProviderKey } from "../../../infrastructure/current_order_provider";
import type { ConfigDto, MobileNewConfigPatchRequest } from "../../../infrastructure/gfo_api/order_api";
import { ApiValidationError } from "../../../infrastructure/gfo_api/api_validation_error";
import ErrorMessage from "../../Shared/ErrorMessage";
import VueDatePicker from "../../Shared/LazyDatePicker";
import { de } from "date-fns/locale";

const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("iway" == app_theme) {
  html = html_iway;
} else if ("lic" == app_theme) {
  html = html_iway;
} else if ("sak" == app_theme) {
  html = html_iway;
}

const WISHDATE_TYPE_ASAP = "asap";
const WISHDATE_TYPE_WISHDATE = "wishdate";

interface Props {
  config: ConfigDto;
}

export default defineComponent({
  template: html,
  components: { ErrorMessage, VueDatePicker },
  props: {
    config: {
      type: Object as () => ConfigDto,
      required: true,
    },
  },
  setup(props: Props) {
    // Injections
    const { t } = useI18n();
    const orderService = inject(orderServiceKey);
    const currentOrderProvider = inject(currentOrderProviderKey);

    // vars und computed
    const generalError = ref("");
    const wishdateType = ref<string>(WISHDATE_TYPE_ASAP);
    const wishdate = ref<Date | null>(null);
    const wishdateError = ref("");

    const wishdateOffsetDays = computed(
      () =>
        currentOrderProvider?.currentOrder?.value?.tenant_context
          ?.wishdate_offset_days,
    );
    const wishdateExcluded = computed(
      () =>
        currentOrderProvider?.currentOrder?.value?.tenant_context
          ?.wishdate_excluded ?? [],
    );

    const wishdateTypeChoices = computed(() => [
      {
        key: WISHDATE_TYPE_ASAP,
        text: t("ConfigsMobile_New_WishdateType_Asap"),
      },
      {
        key: WISHDATE_TYPE_WISHDATE,
        text: t("ConfigsMobile_New_WishdateType_Wishdate"),
      },
    ]);

    // methods
    // laufender Autosave-Request; validate() muss darauf warten, bevor es den
    // (sonst optimistisch geleerten) Fehlerstatus auswertet
    let pendingSave: Promise<void> | null = null;

    const save = async () => {
      const run = async () => {
        wishdateError.value = "";
        generalError.value = "";
        if (orderService == null) {
          return;
        }
        const wishdateStr = ((): string | null => {
          if (null == wishdate.value) {
            return null;
          }
          const yyyy = wishdate.value.getFullYear();
          const mm = String(wishdate.value.getMonth() + 1).padStart(2, "0");
          const dd = String(wishdate.value.getDate()).padStart(2, "0");
          return `${yyyy}-${mm}-${dd}`;
        })();
        const request: MobileNewConfigPatchRequest = {
          wishdate: wishdateStr,
          wishdate_type: wishdateType.value,
        };
        try {
          await orderService.patchConfig(props.config.uuid, props.config.type, request);
        } catch (err) {
          if (err instanceof ApiValidationError) {
            wishdateError.value = err.errors["wishdate"]?.[0] ?? "";
          } else {
            generalError.value = `${err}`;
          }
        }
      };
      pendingSave = run();
      await pendingSave;
    };

    const onWishdateTypeChange = async () => {
      wishdateError.value = "";
      if (WISHDATE_TYPE_ASAP == wishdateType.value) {
        wishdate.value = null;
      }
      await save();
    };

    const onUpdateWishdate = async (evt: Date | null) => {
      wishdate.value = evt;
      wishdateType.value = WISHDATE_TYPE_WISHDATE;
      await save();
    };

    const isValid = computed(() => {
      if (WISHDATE_TYPE_WISHDATE == wishdateType.value && !wishdate.value) {
        return false;
      }
      return !wishdateError.value;
    });

    const validate = async () => {
      if (pendingSave) {
        await pendingSave;
      }
      if (WISHDATE_TYPE_WISHDATE == wishdateType.value && !wishdate.value) {
        wishdateError.value = t("Validation_Field_Required");
      }
      return isValid.value;
    };

    const disabledDates = (dt: Date) => {
      // dt is Date such as Fri, Aug 01 2025 00:00:00 GMT+0200
      // Bei allen Vergleichsdaten sicherstellen, dass auch die Zeit 00:00:00 gesetzt wird.
      const limit_days = wishdateOffsetDays.value ?? 0;
      const earliest = new Date();
      earliest.setHours(0, 0, 0, 0);
      earliest.setDate(earliest.getDate() + limit_days);
      if (dt < earliest) {
        return true;
      }
      const excluded = wishdateExcluded.value;
      if (Array.isArray(excluded)) {
        // Achtung: Mandant-Config verwendet ISO_8601
        // Tag | ISO_8601 | Javascript
        // Mo  | 1        | 1
        // Di  | 2        | 2
        // Mi  | 3        | 3
        // Do  | 4        | 4
        // Fr  | 5        | 5
        // Sa  | 6        | 6
        // So  | 7        | 0
        for (let i = 0; i < excluded.length; i++) {
          const exclusion = excluded[i].trim();
          const match_day = /^D(\d+)$/.exec(exclusion);
          if (match_day) {
            let excluded_day = parseInt(match_day[1], 10);
            if (7 == excluded_day) {
              excluded_day = 0; // Javascript ist 0-basiert
            }
            if (dt.getDay() == excluded_day) {
              return true;
            }
          }
          // Ausschluss bestimmter Daten
          const match_date = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.exec(exclusion);
          if (match_date) {
            const excluded_date = new Date(match_date[0]);
            excluded_date.setHours(0, 0, 0, 0);
            if (dt.toISOString() == excluded_date.toISOString()) {
              return true;
            }
          }
          // Ausschluss bestimmter Daten (wiederholt)
          const match_date_every_year = /^--[0-9]{2}-[0-9]{2}$/.exec(exclusion);
          if (match_date_every_year) {
            const today = new Date();
            const date_str = match_date_every_year[0].replace(
              "--",
              `${today.getFullYear()}-`,
            );
            const excluded_date = new Date(date_str);
            excluded_date.setHours(0, 0, 0, 0);
            if (
              dt.getMonth() == excluded_date.getMonth() &&
              dt.getDate() == excluded_date.getDate()
            ) {
              return true;
            }
          }
        }
      }

      return false;
    };

    onMounted(() => {
      wishdateType.value = props.config.data?.wishdate_type ?? WISHDATE_TYPE_ASAP;
      wishdate.value =
        null == props.config.data?.wishdate ? null : new Date(props.config.data.wishdate);
    });

    return {
      t,
      de,
      generalError,
      wishdateType,
      wishdateTypeChoices,
      wishdate,
      wishdateError,
      disabledDates,
      onWishdateTypeChange,
      onUpdateWishdate,
      isValid,
      validate,
    };
  },
} as any);
